import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { FaqList } from '../components/FaqList'
import { GuideLinks } from '../components/GuideLinks'
import { useStations } from '../data/catalog'
import { Seo } from '../seo/Seo'
import { breadcrumbSchema, faqSchema } from '../seo/schema'
import { absoluteUrl } from '../seo/site'
import { stationsForTopic, type Topic } from '../seo/topics'
import '../seo/copy.css'
import './Stations.css'
import './TopicPage.css'

export function TopicPage({ topic }: { topic: Topic }) {
  const stations = stationsForTopic(topic, useStations())

  return (
    <main className="topic shell">
      <Seo
        title={topic.title}
        description={topic.description}
        path={topic.path}
        keywords={topic.keywords}
        jsonLd={[
          faqSchema(topic.faqs),
          ...(stations.length > 0
            ? [
                {
                  '@context': 'https://schema.org',
                  '@type': 'ItemList',
                  name: topic.h1,
                  numberOfItems: stations.length,
                  itemListElement: stations.map((station, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: absoluteUrl(`/stations/${station.slug}`),
                    name: `${station.name} Jewish music radio`,
                  })),
                },
              ]
            : []),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: topic.nav, path: topic.path },
          ]),
        ]}
      />

      <Breadcrumbs
        items={[
          { name: 'Home', path: '/' },
          { name: topic.nav },
        ]}
      />

      <header className="topic__header">
        <p className="seo-kicker">Music24</p>
        <h1>{topic.h1}</h1>
      </header>

      <div className="seo-prose topic__prose">
        {topic.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>

      <h2>{stations.length === 1 ? 'Station' : 'Stations on now'}</h2>
      {stations.length === 0 ? (
        <p className="topic__empty">
          None of the current stations match this guide.{' '}
          <Link to="/stations">Browse the full catalog</Link>.
        </p>
      ) : (
        <ul className="stations__grid">
          {stations.map((station) => (
            <li key={station.slug} className="stations__item">
              <Link
                className="stations__link"
                to={`/stations/${station.slug}`}
                aria-label={`${station.name} Jewish music radio`}
              >
                <img src={station.icon} alt="" width={88} height={88} />
                <h3>{station.name}</h3>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="topic__more">
        <Link to="/stations">View all stations</Link>
      </p>

      <h2>More Jewish music</h2>
      <GuideLinks current={topic.path} />

      <h2>Questions</h2>
      <FaqList items={topic.faqs} />
    </main>
  )
}
